import { Builder } from '@builder.io/react';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { twMerge } from 'tailwind-merge';

import { ProgressiveImage } from '@videoblocks/storywind';

import { loadBuilderDataOrFallback } from '../../../app/Search/actions/SearchActions';
import { selectSearchBuilderData } from '../../../app/Search/selectors/searchSelectors';
import getAssetSource from '../../../app/Search/utils/getAssetSource';
import { Button } from '../../storyblocks-design-system/components/atoms/Button/Button';
import { Text } from '../../storyblocks-design-system/components/atoms/Text/Text';

interface BrandsAndCreatorsSectionProps {
  urlSlug?: string;
  modelName?: string;
}

const BUILDER_ASSET_BASE =
  'https://cdn.builder.io/api/v1/image/assets%2Fe7fb3d1e4da14573bd2a1edb7bfee5f1%2F';

const BRANDS_AND_CREATORS_FALLBACK = {
  brandsAndCreatorsHeading: 'Loved by 10,000+ brands and creators',
  brandLogos: [
    {
      name: 'Canon',
      alt: 'Canon logo',
      image: `${BUILDER_ASSET_BASE}e2f3e27a524e487898429ea129417a8c`,
    },
    {
      name: 'Univision',
      alt: 'Univision logo',
      image: `${BUILDER_ASSET_BASE}62c39b57ffb14535b47a71fff55e2894`,
    },
    {
      name: 'iHeartMedia',
      alt: 'iHeartMedia logo',
      image: `${BUILDER_ASSET_BASE}1251798c82a64ac3be33b75fe76ff95f`,
    },
    {
      name: 'Live Nation',
      alt: 'Live Nation logo',
      image: `${BUILDER_ASSET_BASE}b3ce0175e50c4c9993a6d9692a1a465a`,
    },
    {
      name: 'BCG',
      alt: 'BCG logo',
      image: `${BUILDER_ASSET_BASE}3030549544a0467e87a6ddde01ec4d46`,
    },
    {
      name: 'ASU',
      alt: 'ASU logo',
      image: `${BUILDER_ASSET_BASE}89b6e085e0e143cfaacbb75377ba0a27`,
    },
    {
      name: 'CBS',
      alt: 'CBS logo',
      image: `${BUILDER_ASSET_BASE}e97c5377fd8746bc832274f79bda0240`,
    },
    {
      name: 'Mayo Clinic',
      alt: 'Mayo Clinic logo',
      image: `${BUILDER_ASSET_BASE}7a1a31899fed46ae87d358d11d052dac`,
    },
    {
      name: 'HarperCollins',
      alt: 'HarperCollins logo',
      image: `${BUILDER_ASSET_BASE}3ab9ddffe718418684d09104eadbceda`,
    },
  ],
  creators: [
    {
      username: '@PremiereBasics',
      alt: 'PremiereBasics',
      image: `${BUILDER_ASSET_BASE}6135f219cee54ec0a6ee09fa69ec1345`,
    },
    {
      username: '@katie',
      alt: 'katie',
      image: `${BUILDER_ASSET_BASE}c3b5362fd982482c9cc1d320890af485`,
    },
    {
      username: '@Daniel_Batal',
      alt: 'Daniel Batal',
      image: `${BUILDER_ASSET_BASE}c4429183cd6b423eb48c37a696a66f3e`,
    },
    {
      username: '@JadeBeason',
      alt: 'Jade Beason',
      image: `${BUILDER_ASSET_BASE}69f5811ea8c842ae88e63fb591d32749`,
    },
    {
      username: '@froknowsphoto',
      alt: 'froknowsphoto',
      image: `${BUILDER_ASSET_BASE}f30847f01fcc421893d3f7b6dd22b4d6`,
    },
  ],
  brandsAndCreatorsCta: { text: 'Get Started Today', href: '/login' },
};

const BrandsAndCreatorsSection = ({
  urlSlug,
  modelName = 'category-search-page',
}: BrandsAndCreatorsSectionProps) => {
  const dispatch: ThunkDispatch<unknown, unknown, never> = useDispatch();
  const builderContent = useSelector(selectSearchBuilderData);

  useEffect(() => {
    if (!urlSlug) return;
    dispatch(
      loadBuilderDataOrFallback(urlSlug, modelName, {
        ...BRANDS_AND_CREATORS_FALLBACK,
        urlSlug,
      })
    );
  }, [urlSlug, modelName, dispatch]);

  const heading =
    builderContent.brandsAndCreatorsHeading ||
    BRANDS_AND_CREATORS_FALLBACK.brandsAndCreatorsHeading;
  const brandLogos = builderContent.brandLogos?.length
    ? builderContent.brandLogos
    : BRANDS_AND_CREATORS_FALLBACK.brandLogos;
  const creators = builderContent.creators?.length
    ? builderContent.creators
    : BRANDS_AND_CREATORS_FALLBACK.creators;
  const cta = builderContent.brandsAndCreatorsCta?.text
    ? builderContent.brandsAndCreatorsCta
    : BRANDS_AND_CREATORS_FALLBACK.brandsAndCreatorsCta;

  const hasContent =
    heading ||
    brandLogos.length > 0 ||
    creators.length > 0 ||
    (cta?.text && cta?.href);
  if (!hasContent) return null;

  return (
    <section className="flex flex-col items-center w-full gap-8 px-6 py-16 md:py-24 md:px-12 lg:px-24">
      {heading && (
        <Text
          fontFamily="polysans"
          fontWeight="semibold"
          fontSize="4xl"
          className="text-center max-w-2xl"
          asChild
        >
          <h2>{heading}</h2>
        </Text>
      )}
      {brandLogos.length > 0 && <BrandLogosRow logos={brandLogos} />}
      {creators.length > 0 && <CreatorsRow creators={creators} />}
      {cta?.text && cta?.href && (
        <Button variant="primary" size="large" href={cta.href}>
          {cta.text}
        </Button>
      )}
    </section>
  );
};

const BrandLogosRow = ({
  logos,
}: {
  logos: { image: string; alt: string; name?: string }[];
}) => (
  <ul
    className="list-none p-0 m-0 flex flex-wrap justify-center lg:grid gap-y-4 w-full items-center grayscale mx-auto"
    style={{
      maxWidth: '1200px',
      gridTemplateColumns: `repeat(${logos.length}, minmax(0, 1fr))`,
    }}
  >
    {logos.map(({ image, alt, name }) => (
      <li
        key={name ?? alt}
        className="flex items-center justify-center w-1/3 md:w-1/5 lg:w-auto p-0.5"
        style={{ height: '59px' }}
      >
        <ProgressiveImage
          className="max-h-full max-w-full object-contain"
          src={getAssetSource(image)}
          alt={alt}
          shouldLazyLoad
        />
      </li>
    ))}
  </ul>
);

const CreatorsRow = ({
  creators,
}: {
  creators: { image: string; username: string; alt: string }[];
}) => (
  <ul className="list-none p-0 m-0 grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-4 w-full max-w-1440px">
    {creators.map(({ image, username, alt }) => (
      <li key={username} className="flex flex-col gap-2">
        <div
          className={twMerge('relative overflow-hidden rounded-xl bg-gray-800')}
          style={{ aspectRatio: '1 / 1' }}
        >
          <ProgressiveImage
            className="absolute inset-0 w-full h-full object-cover"
            src={getAssetSource(image)}
            alt={alt}
            shouldLazyLoad
          />
        </div>
        <Text
          fontFamily="polysans"
          fontWeight="semibold"
          fontSize="base"
          className="text-left"
        >
          <span>{username}</span>
        </Text>
      </li>
    ))}
  </ul>
);

export default BrandsAndCreatorsSection;

Builder.registerComponent(BrandsAndCreatorsSection, {
  name: 'Brands And Creators Section',
  inputs: [],
});
